import React, { RefObject } from 'react';
import { connectDeep } from 'redux-utils';
import styled from 'styled-components';
import { back } from 'redux-first-router';

import App from 'models/app';
import i18n from 'i18n';
import { searchAllApps, searchPreview, searchDeveloperApps, searchDeveloperPreview } from 'actions/registry';
import { setSearchInput } from 'actions/gallery';
import { BackLink } from '../../components/v2/Navigation/BackLink';
import { Typography } from '../../components/v2/Typography';
import { ItemsPreview } from '../../components/v2/ItemsPreview';
import { VirtualizedAppList } from '../../components/v2/AppList/VirtualizedAppList';
import { Transition } from '../../components/v2/Transition';
import { getType, getTypeMapped } from '../../utils.v2';
import { AppList } from '../../components/v2/AppList/AppList';
import { ClockList } from '../../components/v2/ClockList/ClockList';
import { FontIcon } from '../../components/v2/FontIcon';
import { AppType } from '../../constants';

const SearchPageContainer = styled.div`
  background-color: var(--sys-color--surface);
`;

const HomeDiv = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 5px 10px;
  background-color: var(--sys-color--surface);
  color: var(--sys-color--on-surface);
`;

const CenterTitleContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 8px;
`;

const BackDiv = styled.div`
  position: absolute;
  left: 10px;
  padding: 6px;
  display: flex;
  align-items: center;
`;

const SearchDiv = styled.div`
  position: absolute;
  right: 10px;
  padding: 5px 10px;
  width: 41px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

const SearchContainer = styled.div`
  margin: 0 16px;
`;

const SearchInputWrapper = styled.div`
  display: flex;
  height: 56px;
  align-items: center;
  gap: 20px;
  align-self: stretch;
  background: var(--sys-color--surface-container-high);
  border-radius: 28px;
  padding: 0 18px;
  box-sizing: border-box;
`;

const BaseInput = styled.input`
  border: none;
  background: transparent;
  width: 100%;
  height: 100%;
  font-family: inherit;
  font-size: var(--sys-typescale-body-large-size);
  font-weight: var(--sys-typescale-body-large-weight);
  color: var(--sys-color--on-surface);
  outline: none;
  appearance: none;

  &:focus {
    outline: 0;
  }

  &::-webkit-search-cancel-button {
    -webkit-appearance: none;
  }

  &::placeholder {
    color: var(--sys-color--on-surface-variant);
  }

  &:-webkit-autofill,
  &:-webkit-autofill:hover, 
  &:-webkit-autofill:focus {
    -webkit-box-shadow: 0 0 0 1000px var(--sys-color--surface-container-high) inset !important;
    -webkit-text-fill-color: var(--sys-color--on-surface) !important;
    transition: background-color 5000s ease-in-out 0s;
  }
`;

export const FULL_VH = document.documentElement.clientHeight;

const Container = styled.div`
  overflow-y: auto;
  height: ${FULL_VH - 110}px;
  overflow-x: hidden;
  background: var(--sys-color--surface);
`;

const mapStateToProps = (state: any) => {
  const {
    direction,
    searchInput: { text = '', developerName = '', developerProfileId, type }
  } = state.gallery;

  const {
    features: { flags },
  } = state.registry;

  const {
    search,
    searchPreview,
    searchDeveloperApps,
    searchDeveloperPreview,
    installed: {
      loading: installedLoading,
      error: installedError,
      entries: installedEntries = []
    }
  } = state.registry;

  const { isNeutral } = getType(state.location);
  let searchLoading;
  const entries: string[] = [];
  let hasNextPage = false;

  let entriesKey: string;
  let searchPreviewAction;
  let searchAction;
  if (developerProfileId) {
    entriesKey = developerProfileId;
    searchPreviewAction = searchDeveloperPreview;
    searchAction = searchDeveloperApps;
  } else {
    entriesKey = text;
    searchPreviewAction = searchPreview;
    searchAction = search;
  }

  if (isNeutral) {
    const {
      [entriesKey]: { entries: previewEntries = [] } = {},
    } = searchPreviewAction;
    searchLoading = searchPreviewAction.loading;
    entries.push(...previewEntries);
  } else {
    const {
      [`${entriesKey}-${type}`]: {
        entries: specificEntries = [],
        hasNextPage: specificHasNextPage = false
      } = {},
    } = searchAction;
    searchLoading = searchAction.loading;
    hasNextPage = specificHasNextPage;
    entries.push(...specificEntries);
  }

  const loading = installedEntries.length > 0 ? false : installedLoading;

  const error = installedError;

  return {
    loading: loading || (entries.length === 0 && searchLoading) || (!type && !isNeutral),
    error,
    text,
    flags,
    developerName,
    developerProfileId,
    apps: entries.map((appId: string) => App.selectFromState(appId, state)) as App[],
    hasNextPage,
    direction,
    location: state.location
  };
};

interface SearchArgs {
  text: string;
  startIndex?: number;
  type?: string;
}

interface SearchDeveloperAppsArgs {
  developerName?: string;
  developerProfileId: string;
  type?: string;
  startIndex?: number;
}

interface SearchDeveloperPreviewArgs {
  developerName?: string;
  developerProfileId: string;
}

const mapDispatchToProps = (dispatch: any) => ({
  search: ({ text, startIndex, type }: SearchArgs) =>
    dispatch(searchAllApps(type, text, startIndex)),
  searchPreview: ({ text }: SearchArgs) => dispatch(searchPreview(text)),
  searchDeveloperApps: ({ developerName, developerProfileId, type, startIndex }: SearchDeveloperAppsArgs) => dispatch(searchDeveloperApps(
    {
      developerName,
      developerProfileId,
      type,
      startIndex,
    }
  )),
  searchDeveloperPreview: ({ developerName, developerProfileId }: SearchDeveloperPreviewArgs) => dispatch(searchDeveloperPreview(
    developerName,
    developerProfileId,
  )),
  resetSearch: () => dispatch(setSearchInput({ text: '', developerName: '', developerProfileId: undefined, type: null }))
});

type ReduxProps = ReturnType<typeof mapStateToProps> & ReturnType<typeof mapDispatchToProps>;

interface SearchPageProps extends ReduxProps {
  text: string;
  developerName: string;
  developerProfileId: string;
  searchResults: App[];
}

interface SearchPageState {
  text: string;
  developerName: string;
  developerProfileId?: string;
}

class SearchPage extends React.Component<SearchPageProps, SearchPageState> {

  private readonly searchInput: RefObject<any>;

  constructor(props: SearchPageProps) {
    super(props);

    this.searchInput = React.createRef();
    this.state = {
      text: this.props.text,
      developerName: this.props.developerName,
      developerProfileId: this.props.developerProfileId,
    };
  }

  private get tilesEnabled() {
    return this.props.flags.enable_tiles
  }

  componentDidUpdate(prevProps: SearchPageProps, prevState: SearchPageState) {
    const { isNeutral } = getType(this.props.location);
    const { isNeutral: isNeutralPrevious } = getType(prevProps);

    // get search results when ViewAll buttons in search page are pressed
    if (this.props.location.type === 'SEARCH_ITEMS_V2' && prevProps.location.type === 'SEARCH_V2' &&
      this.props.location.kind === 'push') {
      this.getSearchResults();
    }

    // if a new text is added in the detailed search page, when navigating back to the
    // preview page the search results should be updated too
    if (this.props.location.kind === 'pop' && this.props.location.type === 'SEARCH_V2' &&
      prevProps.location.type === 'SEARCH_ITEMS_V2' && this.props.apps.length === 0 &&
      prevProps.apps.length != 0) {
      this.getSearchResults();
    }
  }

  componentWillMount() {
    const { isNeutral } = getType(this.props.location);
    if (!this.props.loading) {
      // Clear the search results when the search page was closed using the Android back button
      if (isNeutral && this.props.location.kind === 'push' && this.props.location.prev.type != 'APP_V2') {
        this.props.resetSearch();
        this.setState({ text: '', developerName: '', developerProfileId: undefined });
      }
    }
  }

  // TODO: replace internal state with updating the Redux text state
  handleChange = (event: any) => {
    const text = event.target.value;
    this.setState({ text, developerName: '', developerProfileId: undefined });
  };

  handleSearch = (event: any) => {
    event.preventDefault();
    this.searchInput.current.blur();

    this.getSearchResults();
  };

  render() {
    let backNavigate;
    const { isNeutral } = getType(this.props.location);

    if (isNeutral) {
      backNavigate = () => {
        this.props.resetSearch();
        back();
      };
    }

    return (
      <SearchPageContainer>
        <HomeDiv>
          <BackDiv>
            <BackLink icon="close" backNavigate={backNavigate} />
          </BackDiv>
          <div>
            <CenterTitleContainer>
              <Typography variant="titleLarge">{i18n('page.Search.header')()}</Typography>
            </CenterTitleContainer>
          </div>
          <SearchDiv />
        </HomeDiv>
        <div>
          <form onSubmit={this.handleSearch} action=".">
            <SearchContainer>
              <SearchInputWrapper>
                <FontIcon name="search" size={24} color="var(--sys-color--on-surface-variant)" />
                <BaseInput
                  ref={this.searchInput}
                  id="search-input"
                  aria-label="search-input"
                  onChange={this.handleChange}
                  placeholder={i18n('page.Search.placeholder')()}
                  value={this.state.text}
                />
              </SearchInputWrapper>
            </SearchContainer>
          </form>
          {this.renderLists()}
        </div>
      </SearchPageContainer>
    );
  }

  private renderLists() {
    const { apps, search, searchDeveloperApps, hasNextPage, loading, error } = this.props;
    const { text, developerName, developerProfileId } = this.state;
    const { appType, isApp, isTile, isNeutral } = getType(this.props.location);
    const appTypeMapped = getTypeMapped(this.props.location);

    if (loading || error) {
      return <Transition error={error} loading={loading} headerHeight={110} />
    }

    if (!isNeutral) {
      let fetchMore;
      if (developerProfileId) {
        fetchMore = ({ startIndex }: any) => searchDeveloperApps({
          developerName,
          developerProfileId,
          type: appType,
          startIndex,
        });
      } else {
        fetchMore = ({ startIndex }: any) => search({
          startIndex,
          text,
          type: appType
        });
      }

      const virtualListProps = {
        apps,
        fetchMore: fetchMore,
        hasNextPage,
        navbarHeight: 110,
      };
      // a check added for small results to help in automation
      const isSmallResultSet = apps.length <= 10;
      return (
        <Container>
          {
            isSmallResultSet ? (
              appType === 'CLOCK' ? (
                <ClockList apps={apps} />
              ) : (
                <AppList apps={apps} />
              )
            ) : (
              <VirtualizedAppList {...virtualListProps} appType={appTypeMapped.appType} />
            )
          }
        </Container>
      );
    }
    const appItems: App[] = [];
    const tileItems: App[] = [];
    const clockItems: App[] = [];
    apps.forEach((app) => {
      if (app.isClock) {
        clockItems.push(app);
      } else if (app.isTile) {
        tileItems.push(app)
      } else {
        appItems.push(app);
      }
    });

    function createOnClickViewAll(type: string) {
      if (!developerProfileId) {
        return undefined;
      }
      return () => {
        searchDeveloperApps({ developerName, developerProfileId, type });
      }
    }

    return (
      <Container>
        {!!appItems.length && <ItemsPreview
          id="view-all-apps"
          name={i18n('page.Home.apps')()}
          isClock={false}
          apps={appItems}
          path={`/v2/search/apps/${text || developerName}`}
          inputText={text || developerName}
          onClickViewAll={createOnClickViewAll("APP")}
        />}
        {this.tilesEnabled && !!tileItems.length && <ItemsPreview
          id="view-all-tiles"
          name={i18n('page.Home.tiles')()}
          isClock={false}
          apps={tileItems}
          path={`/v2/search/tiles/${text}`}
          inputText={text}
          onClickViewAll={createOnClickViewAll("TILE")}
        />}
        {!!clockItems.length && <ItemsPreview
          id="view-all-clocks"
          name={i18n('page.Home.clocks')()}
          isClock={true}
          apps={clockItems}
          path={`/v2/search/clocks/${text || developerName}`}
          inputText={text || developerName}
          onClickViewAll={createOnClickViewAll("CLOCK")}
        />}
      </Container>
    );
  }

  onRecommendationSearch = (text: string) => {
    this.setState({ text, developerName: '', developerProfileId: undefined }, () => {
      this.getSearchResults();
    });
  }

  private getSearchResults() {
    const { text, developerName, developerProfileId } = this.state;
    const { appType, isNeutral } = getTypeMapped(this.props.location);

    if (isNeutral) {
      if (developerProfileId) {
        this.props.searchDeveloperPreview({ developerName, developerProfileId });
      } else {
        this.props.searchPreview({ text });
      }
      return;
    }

    if (developerProfileId) {
      this.props.searchDeveloperApps({
        developerName,
        developerProfileId,
        type: appType.toUpperCase(),
      });
    } else {
      this.props.search({
        text,
        type: appType.toUpperCase(),
      });
    }
  }
}

export default connectDeep(mapStateToProps, mapDispatchToProps)(SearchPage);
